import {createSelector} from 'reselect';

import {
    getAbacusTests,
    getListing,
    getSystem,
    getTranslations,
    getTravelerFeatures,
    getTripData
} from './store';
import {getFirstPayment, getPriceDetails} from './priceDetails';
import getRateDetailsData from './rateDetails';
import {getListingPageModalStates} from './modalStates';
import getAvailabilityCalendar from './availabilityCalendar';
import getBookingUrl from './bookingUrl';
import getBookingModalData from './bookingModal';
import {getJsReviews, getBearStayBannerLabels} from './reviews';
import {getListingSummary} from './listingSummary';
import {getIsTypeaheadInputFocused} from './listingPageState';
import {getViewDetailsTotalData} from './rightRail';
import {getMapData} from './map';
import {getDates, getGuests} from './searchBox';
import {getBaseUrlPath, getBrandName, getBrowserName, getSiteName} from './system';
import {getTravelerSegmentFeatures} from './travelerSegmentFeatures';
import {
    getAccomodationSummary,
    getBearStayReviewData,
    getBookingType,
    getDescription,
    getPropertyName,
    getSearchLandingPages,
    getListingIsFamilyFriendly,
    getVirtualTourData
} from './listing';
import {getMainImageHeight} from './imageCarousel';
import {getMultiUnitsLayoutValues} from './multiUnits';
import getHouseRules from './houseRules';
import {cancellationPolicySelector} from './cancellationPolicy';

const getListingDescription = createSelector(
    [getAccomodationSummary, getDescription, getPropertyName],
    (accomodationSummary, description, propertyName) => {
        return {
            propertyName,
            summary: accomodationSummary,
            description
        };
    }
);

const getReviewsScrollSelector = createSelector(
    [getJsReviews],
    (reviewData) => {
        return reviewData.reviewId ? `[data-review="${reviewData.reviewId}"]` : '';
    }
);

const getReviewsModalScrollTop = createSelector(
    [getJsReviews],
    (reviewData) => {
        return reviewData.scrollTop;
    }
);

const getSlpBreadcrumbLinks = createSelector(
    [getSearchLandingPages],
    (searchLandingPages) => {
        if (!searchLandingPages) {
            return [];
        }
        const {breadcrumbs = []} = searchLandingPages;
        const targetNum = 8;
        const linkArray = [];

        for (let crumb of breadcrumbs) {
            if (linkArray.length === targetNum) {
                break;
            }
            if (crumb.path && crumb.url) {
                linkArray.push({text: crumb.name, url: crumb.url});
            }
        }

        return [{links: linkArray, isBreadcrumbs: true}];
    }
);

const getDisplayImprint = createSelector(
    [getTravelerFeatures],
    (travelerFeatures) => {
        return !!travelerFeatures.displayImprint;
    }
);

const getIsFamilySegment = createSelector(
    [getTripData],
    (tripData) => {
        return !!(tripData && tripData.isFamilySegment);
    }
);

const getShowMapSection = createSelector(
    [getMapData],
    (mapData) => {
        return !!(mapData.lat && mapData.lng);
    }
);

const getDefaultReviewsContainerComponentHeight = createSelector(
    [getListingSummary, getSystem],
    (listingSummary, system) => {
        const {reviewCount} = listingSummary;
        const {reviewPageSize} = system;
        const defaultReviewHeight = 300;

        if (!reviewCount) {
            return '480px';
        }

        // calculate approximate reviews section height based on the
        // number of reviews to reduce pushing content bottom after reviews load
        if (reviewCount > reviewPageSize) {
            return `${reviewPageSize * defaultReviewHeight}px`;
        }
        return `${reviewCount * defaultReviewHeight}px`;
    }
);

const getListingPageData = createSelector(
    [
        getAbacusTests,
        getAvailabilityCalendar,
        getBearStayBannerLabels,
        getBearStayReviewData,
        getBookingModalData,
        getBookingType,
        getBookingUrl,
        getBrandName,
        getBrowserName,
        getDates,
        getGuests,
        getSiteName,
        getIsFamilySegment,
        getIsTypeaheadInputFocused,
        getListingDescription,
        getBaseUrlPath,
        getHouseRules,
        getListing,
        getListingPageModalStates,
        getListingSummary,
        getPriceDetails,
        getRateDetailsData,
        getReviewsModalScrollTop,
        getReviewsScrollSelector,
        getSlpBreadcrumbLinks,
        getShowMapSection,
        getSystem,
        getTranslations,
        getTravelerFeatures,
        getTravelerSegmentFeatures,
        getDisplayImprint,
        getListingIsFamilyFriendly,
        getVirtualTourData,
        getFirstPayment,
        getViewDetailsTotalData,
        getMainImageHeight,
        getMultiUnitsLayoutValues,
        cancellationPolicySelector,
        getDefaultReviewsContainerComponentHeight
    ],
    (
        abacusTestData,
        availabilityCalendar,
        bearStayBannerLabels,
        bearStayReviewData,
        bookingModalData,
        bookingType,
        bookingUrl,
        brandName,
        browserName,
        dates,
        guests,
        siteName,
        isFamilySegment,
        isTypeaheadInputFocused,
        listingDescription,
        baseUrlPath,
        houseRules,
        listing,
        listingPageModalStates,
        listingSummary,
        priceDetails,
        rateDetailsData,
        reviewsModalScrollTop,
        reviewsScrollSelector,
        breadcrumbLinks,
        showMapSection,
        system,
        translations,
        travelerFeatures,
        travelerSegmentFeatures,
        displayImprint,
        isFamilyFriendly,
        virtualTourData,
        firstPaymentData,
        viewDetailsTotalData,
        mainImageHeight,
        multiUnitLayoutValues,
        cancellationPolicy,
        defaultReviewsContainerComponentHeight
    ) => {
        const {
            showTopRecoCarousel,
            showMidpageRecoCarousel,
            showBackToSerp,
            useUrgencyMessageExperiment,
            showExpandedSearchForm
        } = travelerSegmentFeatures;
        const {displayCivilCodeDisclaimerLink, isLoggedIn} = travelerFeatures;
        const {virtualTourId, virtualTourUrl, virtualTourHeroImage} = virtualTourData;

        return {
            abacusTestData,
            availabilityCalendar,
            bearStayBannerLabels,
            bearStayReviewData,
            bookingModalError: bookingModalData.currentError,
            bookingUrl,
            brandName,
            currencyConversionLabel: priceDetails.currencyConversionLabel,
            dates,
            guests,
            siteName,
            displayCivilCodeDisclaimerLink,
            isFamilySegment,
            isIpm: listing.integratedPropertyManager,
            isLoggedIn,
            isTypeaheadInputFocused,
            labels: translations,
            listingDescription,
            baseUrlPath,
            listingPageModalStates,
            listingSummary,
            propertyOlb: bookingType.onlineBookable,
            showRateDetails: rateDetailsData.showRateDetails,
            reviewsModalScrollTop,
            reviewsScrollSelector,
            breadcrumbLinks,
            showMapSection,
            showChat: browserName !== 'ie',
            showHouseRules: houseRules.showHouseRules,
            showMidpageRecoCarousel:
                showMidpageRecoCarousel && !multiUnitLayoutValues.showMultiUnits,
            showTopRecoCarousel,
            showBackToSerp,
            useUrgencyMessageExperiment,
            showExpandedSearchForm,
            system,
            displayImprint,
            isFamilyFriendly,
            virtualTourId,
            virtualTourUrl,
            virtualTourHeroImage,
            firstPaymentData,
            viewDetailsTotalData,
            navigationContainerHeight: mainImageHeight + 16,
            isTravelerLoggedIn: travelerFeatures.isLoggedIn,
            cancellationPolicy,
            defaultReviewsContainerComponentHeight
        };
    }
);

export default getListingPageData;
export {
    getListingDescription,
    getListingPageData,
    getReviewsModalScrollTop,
    getShowMapSection,
    getSlpBreadcrumbLinks,
    getDefaultReviewsContainerComponentHeight
};
